import React, { useState, useEffect } from 'react';
import './SearchBar.css';

const SearchBar = ({ conversation, onSearchResults, isVisible, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(-1);

    useEffect(() => {
        if (searchTerm.trim()) {
            const searchResults = [];
            conversation.forEach((message, messageIndex) => {
                if (message.msg.toLowerCase().includes(searchTerm.toLowerCase())) {
                    const text = message.msg;
                    const searchIndex = text.toLowerCase().indexOf(searchTerm.toLowerCase());
                    const start = Math.max(0, searchIndex - 50);
                    const end = Math.min(text.length, searchIndex + searchTerm.length + 50);
                    const preview = text.substring(start, end);
                    
                    searchResults.push({
                        messageIndex,
                        preview,
                        from: message.from,
                        fullText: text,
                        searchIndex
                    });
                }
            });
            setResults(searchResults);
            setCurrentResultIndex(searchResults.length > 0 ? 0 : -1);
            onSearchResults(searchResults);
        } else {
            setResults([]);
            setCurrentResultIndex(-1);
            onSearchResults([]);
        }
    }, [searchTerm, conversation, onSearchResults]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) {
                navigateResults('prev');
            } else {
                navigateResults('next');
            }
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const navigateResults = (direction) => {
        if (results.length === 0) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = currentResultIndex < results.length - 1 ? currentResultIndex + 1 : 0;
        } else {
            newIndex = currentResultIndex > 0 ? currentResultIndex - 1 : results.length - 1;
        }
        
        setCurrentResultIndex(newIndex);
        
        // Scroll to the result
        const messageElement = document.querySelector(`[data-message-index="${results[newIndex].messageIndex}"]`);
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            messageElement.classList.add('highlighted');
            setTimeout(() => messageElement.classList.remove('highlighted'), 2000);
        }
    };

    const highlightText = (text, searchTerm) => {
        if (!searchTerm) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<mark>$1</mark>');
    };

    if (!isVisible) return null;

    return (
        <div className="search-overlay">
            <div className="search-bar">
                <div className="search-input-container">
                    <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input
                        type="text"
                        placeholder="Search in conversation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    <div className="search-controls">
                        {results.length > 0 && (
                            <>
                                <span className="search-count">
                                    {currentResultIndex + 1} of {results.length}
                                </span>
                                <button 
                                    onClick={() => navigateResults('prev')}
                                    className="nav-button"
                                    title="Previous result (Shift+Enter)"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15,18 9,12 15,6"></polyline>
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => navigateResults('next')}
                                    className="nav-button"
                                    title="Next result (Enter)"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9,18 15,12 9,6"></polyline>
                                    </svg>
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="close-search" title="Close search (Esc)">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>
                
                {results.length > 0 && (
                    <div className="search-results">
                        {results.map((result, index) => (
                            <div 
                                key={index}
                                className={`search-result ${index === currentResultIndex ? 'active' : ''}`}
                                onClick={() => {
                                    setCurrentResultIndex(index);
                                    const messageElement = document.querySelector(`[data-message-index="${result.messageIndex}"]`);
                                    if (messageElement) {
                                        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                }}
                            >
                                <div className="result-meta">
                                    <span className={`result-from ${result.from}`}>
                                        {result.from === 'user' ? 'You' : 'AI'}
                                    </span>
                                </div>
                                <div 
                                    className="result-preview"
                                    dangerouslySetInnerHTML={{ 
                                        __html: highlightText(result.preview, searchTerm) 
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchBar;